import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { Wrench, Truck, PiggyBank, ArrowUpRight } from "lucide-react";

export default function ShopSavingsCard({ year }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/internal-savings?year=${year}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [year]);

  if (!data) return null;
  const { repair, trucking, combined_total, top_jobs } = data;
  if (combined_total <= 0) return null;  // hide until there's something to show

  return (
    <div className="bg-[#1F1E1B] text-white border-2 border-[#1F1E1B] mb-8" data-testid="shop-savings-card">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-white/15 bg-[#0F0E0C]">
        <PiggyBank className="w-5 h-5" strokeWidth={2.2} />
        <div>
          <div className="font-display text-lg font-black uppercase tracking-wider">In-House Shop Savings</div>
          <div className="text-[11px] text-white/60">
            What it would have cost to outsource the Repair + Trucking work the Farm got done internally in {year}.
          </div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Total Value</div>
          <div className="font-display text-3xl font-black tabular text-[#E5A835]" data-testid="savings-total">
            {fmtUSD(combined_total)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Repair */}
        <div className="p-5 border-b md:border-b-0 md:border-r border-white/15" data-testid="savings-repair">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4" />
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">J&K Repair</div>
          </div>
          <div className="font-display text-2xl font-black tabular text-[#E5A835] mb-2">
            {fmtUSD(repair.total)}
          </div>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <Stat label="Jobs" value={repair.invoice_count} />
            <Stat label="Labor hrs" value={fmtNum(repair.labor_hours, 1)} />
            <Stat label="Parts $" value={fmtUSD(repair.parts_cost)} />
          </div>
        </div>

        {/* Trucking */}
        <div className="p-5" data-testid="savings-trucking">
          <div className="flex items-center gap-2 mb-3">
            <Truck className="w-4 h-4" />
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">J&K Trucking</div>
          </div>
          <div className="font-display text-2xl font-black tabular text-[#E5A835] mb-2">
            {fmtUSD(trucking.total)}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <Stat label="Hauls" value={trucking.invoice_count} />
            <Stat label="Miles" value={fmtNum(trucking.miles, 0)} />
          </div>
        </div>
      </div>

      {top_jobs?.length > 0 && (
        <div className="border-t border-white/15 px-5 py-3" data-testid="savings-top-jobs">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2">
            Top {Math.min(top_jobs.length, 5)} biggest internal jobs
          </div>
          <ul className="space-y-1">
            {top_jobs.map((j) => (
              <li key={j.id} className="flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1 text-white/60 shrink-0">
                  {j.business_id === "jk_repair" ? <Wrench className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                  {j.invoice_number}
                </span>
                <span className="text-white/70 truncate">{j.summary || "—"}</span>
                <span className="ml-auto tabular font-bold text-[#E5A835] shrink-0">{fmtUSD(j.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="px-5 py-2 border-t border-white/15 bg-[#0F0E0C] flex items-center justify-between text-[11px]">
        <span className="text-white/50">Auto-mirrored to Farm expenses · category internal-repair / internal-trucking.</span>
        <Link to="/expenses" className="text-[#E5A835] hover:underline inline-flex items-center gap-1 font-bold uppercase tracking-wider" data-testid="savings-view-expenses">
          View Farm expenses <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-white/50 uppercase tracking-wider text-[9px]">{label}</div>
      <div className="font-bold tabular text-sm">{value}</div>
    </div>
  );
}
